import { z } from 'zod';

// Password complexity regex - must match backend requirements
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.,;:_\-+=#^(){}[\]<>])[A-Za-z\d@$!%*?&.,;:_\-+=#^(){}[\]<>]{8,}$/;

const passwordValidation = z
  .string()
  .min(1, 'validation.required')
  .min(8, 'validation.passwordMinLength')
  .regex(PASSWORD_REGEX, 'validation.passwordComplexity');

// Address schema
export const addressSchema = z.object({
  country: z.string().min(1, 'validation.required'),
  countryCode: z.string().optional(),
  city: z.string().min(1, 'validation.required'),
  street: z.string().optional(),
  postalCode: z.string().optional(),
  landmark: z.string().optional(),
});

export type AddressFormData = z.infer<typeof addressSchema>;

// Profile schema (uses name instead of firstName/lastName)
export const profileSchema = z.object({
  name: z.string().min(1, 'validation.required').min(2, 'validation.minLength'),
  birthday: z.string().optional(),
  address: addressSchema.optional(),
  image: z.string().optional(),
});

export type ProfileFormData = z.infer<typeof profileSchema>;

// Settings change password schema (uses oldPassword to match server API)
export const settingsChangePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'validation.required'),
    newPassword: passwordValidation,
    confirmPassword: z.string().min(1, 'validation.required'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'validation.passwordMatch',
    path: ['confirmPassword'],
  });

export type SettingsChangePasswordFormData = z.infer<typeof settingsChangePasswordSchema>;

// Change email schema
export const changeEmailSchema = z
  .object({
    password: z.string().min(1, 'validation.required'),
    newEmail: z.string().min(1, 'validation.required').email('validation.email'),
    confirmEmail: z.string().min(1, 'validation.required').email('validation.email'),
  })
  .refine((data) => data.newEmail === data.confirmEmail, {
    message: 'settings.email.emailMismatch',
    path: ['confirmEmail'],
  });

export type ChangeEmailFormData = z.infer<typeof changeEmailSchema>;

// Delete account schema
export const deleteAccountSchema = z.object({
  password: z.string().min(1, 'validation.required'),
  reason: z.string().optional(),
  confirmation: z.string().min(1, 'validation.required'),
});

export type DeleteAccountFormData = z.infer<typeof deleteAccountSchema>;

// Preferences schema
export const preferencesSchema = z.object({
  language: z.enum(['fr', 'en', 'ar']),
  theme: z.enum(['light', 'dark', 'system']),
  currency: z.string().optional(),
});

export type PreferencesFormData = z.infer<typeof preferencesSchema>;
